import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { TIER_RANK_MAP, type TierLevel } from '../../domain/enums/TierLevel';
import { useAuthStore } from '../../application/stores/authStore';
import { restoreSession } from '../../application/hooks/useAuth';
import { useTranslation } from '../../application/hooks/useTranslation';

/**
 * On a fresh page load the guard first waits for the silent cookie refresh (restoreSession) so a reload
 * keeps the user signed in; only when that fails do unauthenticated users bounce to /login with the intended route remembered.
 * The route is recorded in an effect — calling a store setter during render trips
 * React's "setState while rendering another component" guard (caught by the runtime
 * proof in a real browser; RTL never hit this path because tests start at /login).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const restoreAttempted = useAuthStore((s) => s.restoreAttempted);
  const { t } = useTranslation();
  const location = useLocation();
  useEffect(() => {
    if (!restoreAttempted) void restoreSession();
  }, [restoreAttempted]);
  useEffect(() => {
    if (!token && restoreAttempted) {
      useAuthStore.getState().setIntendedRoute(location.pathname + location.search);
    }
  }, [token, restoreAttempted, location]);
  // Hold until restore settles: refresh stores the token before /users/me returns, and rendering then
  // would show tier-gated routes as access-denied (no user → L1) for a moment.
  if (!restoreAttempted) {
    return (
      <div className="state-box" role="status" aria-busy="true" data-testid="session-restoring">
        <p>{t('common.loading')}</p>
      </div>
    );
  }
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Tier gate: below-minTier sessions see an explicit access panel (403 semantics). */
export function RequireTier({ minTier, children }: { minTier: TierLevel; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation();
  const rank = user ? TIER_RANK_MAP[user.tier] : 0;
  if (rank < TIER_RANK_MAP[minTier]) {
    return (
      <div className="state-box state-error" role="alert" data-testid="access-denied">
        <ShieldAlert size={20} aria-hidden />
        <p>{t('access.denied')}</p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Subscriber-only gate: executives (L2-L6) have no installments or payment options, so they are sent back to the
 * dashboard. Owners (L1) pass through. Mirrors RequireTier's reliance on the already-restored session user.
 */
export function RequireSubscriber({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const rank = user ? TIER_RANK_MAP[user.tier] : 0;
  if (rank >= 2) return <Navigate to="/" replace />;
  return <>{children}</>;
}
